import { createApiClient, getApiErrorInfo } from './api';
import { config } from '../config';
import type { AxiosInstance } from 'axios';

export const TENANT_LOGO_UPDATED_EVENT = 'tenant-logo-updated';

export interface TenantStats {
  totalUsers: number;
  activeEnrollments: number;
  completedCourses: number;
}

export type CompletedCoursesRange = 'all' | 'ytd';

/**
 * Service for tenant-related operations
 * Handles tenant determination and tenant API calls with caching
 */
class TenantService {
  private api: AxiosInstance;

  constructor() {
    this.api = createApiClient(config.api.tenants);
  }

  // Cache for tenant data to prevent multiple API calls
  private tenantCache = new Map<string, any>();
  private logoCache = new Map<string, string>();
  private cacheExpiry = new Map<string, number>();
  /** Bumped on upload/remove so the next GET bypasses any HTTP-cached copy of the old logo. */
  private logoVersions = new Map<string, number>();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  /**
   * Manually set tenant ID (e.g., from App.tsx)
   */
  private currentTenantId: string | null = null;

  /**
   * Set the tenant ID manually
   * @param tenantId Tenant ID string
   */
  setTenantId(tenantId: string | null) {
    this.currentTenantId = tenantId;
  }

  /**
   * Determine tenant ID based on various strategies
   * Priority: URL param > Subdomain > Default
   * @returns Tenant ID string
   */
  async determineTenantId(): Promise<string> {
    // Strategy 0: Use manually set tenantId if available
    if (this.currentTenantId) {
      console.log('📍 Tenant determined from manual set:', this.currentTenantId);
      return this.currentTenantId;
    }  

    // Strategy 2: Use default tenant
    const defaultTenant = this.getDefaultTenant();
    console.log('📍 Using default tenant:', defaultTenant);
    return defaultTenant;
  }  

  /**
   * Get default tenant ID
   * You can customize this based on your requirements
   */
  private getDefaultTenant(): string {
    return config.defaultTenantId;
  }

  /**
   * Fetch all available tenants
   * Restricted to admin users only
   */
  async getAllTenants(): Promise<Array<{ id: string; name: string; subdomain?: string }>> {
    try {
      const response = await this.api.get('/api/tenants');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
      return [];
    }
  }

  /**
   * Fetch tenant details by ID with caching
   */
  async getTenantById(tenantId: string): Promise<any> {
    // Check cache first and if not expired
    const cacheKey = `tenant_${tenantId}`;
    const now = Date.now();
    const expiryTime = this.cacheExpiry.get(cacheKey);
    
    if (this.tenantCache.has(cacheKey) && expiryTime && now < expiryTime) {
      console.log('🎯 Using cached tenant data for:', tenantId);
      return this.tenantCache.get(cacheKey);
    }

    try {
      console.log('🌐 Fetching tenant data from API for:', tenantId);
      const response = await this.api.get(`/api/tenants/${tenantId}`);
      
      // Cache the result
      this.tenantCache.set(cacheKey, response.data);
      this.cacheExpiry.set(cacheKey, now + this.CACHE_DURATION);
      
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch tenant ${tenantId}:`, error);
      return null;
    }
  }

  /**
   * Fetch tenant logo with caching
   */
  async getTenantLogo(tenantId: string): Promise<string | null> {
    // Check cache first and if not expired
    const cacheKey = `logo_${tenantId}`;
    const now = Date.now();
    const expiryTime = this.cacheExpiry.get(cacheKey);
    
    if (this.logoCache.has(cacheKey) && expiryTime && now < expiryTime) {
      // Empty string is the negative-cache marker for "tenant has no logo"
      return this.logoCache.get(cacheKey) || null;
    }

    try {
      const version = this.logoVersions.get(tenantId);
      const url = version
        ? `/api/tenants/${tenantId}/logo?v=${version}`
        : `/api/tenants/${tenantId}/logo`;
      const response = await this.api.get(url, { responseType: 'blob' });

      // 204 = tenant exists but has no custom logo. Negative-cache so we
      // don't re-request it on every render.
      if (response.status === 204) {
        this.logoCache.set(cacheKey, '');
        this.cacheExpiry.set(cacheKey, now + this.CACHE_DURATION);
        return null;
      }

      const logoBlob = response.data;
      const logoUrl = URL.createObjectURL(logoBlob);

      // Cache the result
      this.logoCache.set(cacheKey, logoUrl);
      this.cacheExpiry.set(cacheKey, now + this.CACHE_DURATION);

      return logoUrl;
    } catch (error) {
      const apiError = getApiErrorInfo(error);
      if (apiError.status === 404) {
        // Expected: tenant has no custom logo. Negative-cache so we don't
        // re-request it on every render.
        this.logoCache.set(cacheKey, '');
        this.cacheExpiry.set(cacheKey, now + this.CACHE_DURATION);
        return null;
      }
      console.error(`Failed to fetch logo for tenant ${tenantId}:`, error);
      return null;
    }
  }

  /**
   * Get tenant details with logo in a single consolidated call
   */
  async getTenantWithLogo(tenantId: string): Promise<{ tenant: any; logoUrl: string | null }> {
    const [tenant, logoUrl] = await Promise.all([
      this.getTenantById(tenantId),
      this.getTenantLogo(tenantId)
    ]);

    return { tenant, logoUrl };
  }

  /**
   * Clear cache for a specific tenant or all tenants
   */
  clearCache(tenantId?: string) {
    if (tenantId) {
      const tenantKey = `tenant_${tenantId}`;
      const logoKey = `logo_${tenantId}`;
      
      // Clean up blob URL to prevent memory leaks
      const logoUrl = this.logoCache.get(logoKey);
      if (logoUrl && logoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(logoUrl);
      }
      
      this.tenantCache.delete(tenantKey);
      this.logoCache.delete(logoKey);
      this.cacheExpiry.delete(tenantKey);
      this.cacheExpiry.delete(logoKey);
      console.log('🧹 Cleared cache for tenant:', tenantId);
    } else {
      // Clean up all blob URLs to prevent memory leaks
      for (const logoUrl of this.logoCache.values()) {
        if (logoUrl && logoUrl.startsWith('blob:')) {
          URL.revokeObjectURL(logoUrl);
        }
      }
      
      this.tenantCache.clear();
      this.logoCache.clear();
      this.cacheExpiry.clear();
      console.log('🧹 Cleared all tenant cache');
    }
  }

  /**
   * Refresh tenant data by clearing cache and fetching fresh data
   */
  async refreshTenant(tenantId: string): Promise<{ tenant: any; logoUrl: string | null }> {
    this.clearCache(tenantId);
    return this.getTenantWithLogo(tenantId);
  }

  /**
   * Create a new tenant
   */
  async createTenant(tenantData: {
    name: string;
    isActive?: boolean;
  }): Promise<{ id: string; name: string }> {
    try {
      console.log('🔄 Creating new tenant:', tenantData.name);
      const response = await this.api.post('/api/tenants', tenantData);
      
      // Clear all cache since we're adding a new tenant
      this.clearCache();
      
      console.log('✅ Tenant created successfully:', response.data);
      const tenantId = response.data.id; // Access the `id` from the `data` property
      return { id: tenantId, name: tenantData.name }; // Return both ID and name
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      console.error('❌ Failed to create tenant:', error);
      throw new Error(apiError.message || 'Failed to create tenant');
    }
  }

  /**
   * Create a new business tenant and assign the current user as tenant-admin.
   * Uses the dedicated /api/tenants/signup endpoint which handles tenant creation,
   * user-tenant association, and role promotion in a single atomic operation.
   */
  async createBusinessTenant(businessData: {
    companyName: string;
    adminUser: {
      authProviderId: string;
      email: string;
      firstName: string;
      lastName: string;
    };
    selectedModules?: string[];
    billingPeriod?: string;
  }): Promise<{ tenant: any; user: any }> {
    try {
      console.log('🔄 Creating business tenant:', businessData.companyName);

      const response = await this.api.post('/api/tenants/signup', {
        companyName: businessData.companyName,
        firstName: businessData.adminUser.firstName,
        lastName: businessData.adminUser.lastName,
      });

      const { tenant, user } = response.data;

      console.log('✅ Business tenant created and user assigned successfully');
      if (businessData.selectedModules || businessData.billingPeriod) {
        console.log('📋 Business setup:', {
          tenant: tenant.name,
          modules: businessData.selectedModules,
          billing: businessData.billingPeriod
        });
      }

      // Clear tenant cache since we created a new one
      this.clearCache();

      return { tenant, user };
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      console.error('❌ Failed to create business tenant:', error);
      throw new Error(apiError.message || 'Failed to create business account');
    }
  }

  /**
   * Update tenant data
   */
  async updateTenant(tenantId: string, tenantData: {
    name?: string;
    isActive?: boolean;
  }): Promise<any> {
    try {
      console.log('🔄 Updating tenant:', tenantId);
      const response = await this.api.put(`/api/tenants/${tenantId}`, tenantData);
      
      // Clear cache for updated tenant
      this.clearCache(tenantId);
      
      console.log('✅ Tenant updated successfully:', response.data);
      return response.data;
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      console.error('❌ Failed to update tenant:', error);
      throw new Error(apiError.message || 'Failed to update tenant');
    }
  }

  /**
   * Upload logo for a tenant
   */
  async uploadTenantLogo(tenantId: string, logoFile: File): Promise<void> {
    try {
      console.log('🔄 Uploading logo for tenant:', tenantId);
      const logoFormData = new FormData();
      logoFormData.append('logo', logoFile);
      
      await this.api.post(`/api/tenants/${tenantId}/logo`, logoFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      this.invalidateLogo(tenantId);
      console.log('✅ Logo uploaded successfully');
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      console.error('❌ Failed to upload logo:', error);
      throw new Error(apiError.message || 'Failed to upload logo');
    }
  }

  /**
   * Fetch tenant dashboard summary stats
   */
  async getTenantStats(tenantId: string, completedCoursesRange: CompletedCoursesRange = 'all'): Promise<TenantStats> {
    try {
      const response = await this.api.get(`/api/tenants/${tenantId}/stats?completedCoursesRange=${completedCoursesRange}`);
      return response.data;
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      console.error('❌ Failed to fetch tenant stats:', error);
      throw new Error(apiError.message || 'Failed to fetch tenant stats');
    }
  }

  /**
   * Remove a tenant's logo. The AppBar falls back to the CPAx mark.
   */
  async deleteTenantLogo(tenantId: string): Promise<void> {
    try {
      await this.api.delete(`/api/tenants/${tenantId}/logo`);
      this.invalidateLogo(tenantId);
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      console.error('❌ Failed to remove logo:', error);
      throw new Error(apiError.message || 'Failed to remove logo', { cause: error });
    }
  }

  /**
   * Drop every cached copy of a tenant's logo (in-memory blob and the browser
   * HTTP cache, via a version query) and tell listeners such as the AppBar to refetch.
   */
  private invalidateLogo(tenantId: string): void {
    const logoKey = `logo_${tenantId}`;
    const cachedLogoUrl = this.logoCache.get(logoKey);
    if (cachedLogoUrl && cachedLogoUrl.startsWith('blob:')) {
      URL.revokeObjectURL(cachedLogoUrl);
    }
    this.logoCache.delete(logoKey);
    this.cacheExpiry.delete(logoKey);

    const updatedAt = Date.now();
    this.logoVersions.set(tenantId, updatedAt);

    window.dispatchEvent(new CustomEvent(TENANT_LOGO_UPDATED_EVENT, {
      detail: { tenantId, updatedAt },
    }));
  }

  /**
   * Delete tenant
   */
  async deleteTenant(tenantId: string): Promise<void> {
    try {
      console.log('🔄 Deleting tenant:', tenantId);
      await this.api.delete(`/api/tenants/${tenantId}`);
      
      // Clear cache for deleted tenant
      this.clearCache(tenantId);
      
      console.log('✅ Tenant deleted successfully');
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      console.error('❌ Failed to delete tenant:', error);
      throw new Error(apiError.message || 'Failed to delete tenant');
    }
  }
}

export const tenantService = new TenantService();
export default tenantService;
